import React, { useState } from 'react';
import { InlineMath, BlockMath } from 'react-katex';

const MathInputHelp = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('basics');

  if (!isOpen) return null;

  const examples = {
    basics: [
      {
        title: 'Exponents',
        input: '23^e or x^2',
        output: '23^e \\text{ or } x^2',
        description: 'Use ^ for exponents. Wrap complex expressions in {} like x^{2+3}'
      },
      {
        title: 'Square Roots',
        input: 'sqrt(x) or sqrt{x}',
        output: '\\sqrt{x}',
        description: 'Use sqrt() or sqrt{} for square roots'
      },
      {
        title: 'Greek Letters',
        input: 'pi, theta, alpha, beta',
        output: '\\pi, \\theta, \\alpha, \\beta',
        description: 'Type Greek letter names directly'
      }
    ],
    functions: [
      {
        title: 'Trigonometric Functions',
        input: 'sin(x), cos(x), tan(x)',
        output: '\\sin(x), \\cos(x), \\tan(x)',
        description: 'Standard trig functions'
      },
      {
        title: 'Inverse Trig Functions',
        input: 'sin^-1(x), arcsin(x)',
        output: '\\sin^{-1}(x), \\arcsin(x)',
        description: 'Use ^-1 notation or arc prefix'
      },
      {
        title: 'Logarithms',
        input: 'ln x, log_2 x, log_10(x)',
        output: '\\ln x, \\log_{2} x, \\log_{10}(x)',
        description: 'Natural log, log with base notation'
      },
      {
        title: 'Hyperbolic Functions',
        input: 'sinh(x), cosh(x), tanh(x)',
        output: '\\sinh(x), \\cosh(x), \\tanh(x)',
        description: 'Hyperbolic functions'
      }
    ],
    calculus: [
      {
        title: 'Integrals',
        input: 'int f(x) dx',
        output: '\\int f(x) \\, dx',
        description: 'Basic integral notation'
      },
      {
        title: 'Definite Integrals',
        input: 'int_0^1 x^2 dx',
        output: '\\int_{0}^{1} x^2 \\, dx',
        description: 'Use underscore for lower limit, caret for upper limit'
      },
      {
        title: 'Derivatives',
        input: "f'(x) or df/dx",
        output: "f'(x) \\text{ or } \\frac{df}{dx}",
        description: 'Prime notation or differential notation'
      }
    ],
    symbols: [
      {
        title: 'Infinity',
        input: 'infinity or infty',
        output: '\\infty',
        description: 'Type infinity or infty'
      },
      {
        title: 'Inequalities',
        input: '<= >= != ~=',
        output: '\\leq \\geq \\neq \\approx',
        description: 'Comparison operators'
      },
      {
        title: 'Sets',
        input: 'in, subset, cup, cap',
        output: '\\in, \\subset, \\cup, \\cap',
        description: 'Set theory symbols'
      }
    ]
  };

  const tabs = [
    { id: 'basics', label: 'Basics', icon: '📝' },
    { id: 'functions', label: 'Functions', icon: '🔢' },
    { id: 'calculus', label: 'Calculus', icon: '∫' },
    { id: 'symbols', label: 'Symbols', icon: '∞' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">📚</div>
            <div>
              <h2 className="text-xl font-bold">Math Input Help</h2>
              <p className="text-blue-100 text-sm">Learn how to type mathematical expressions</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="text-lg">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-6">
            {examples[activeTab].map((example, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-2">
                    {index + 1}
                  </span>
                  {example.title}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Type this:
                    </label>
                    <div className="bg-white border border-gray-300 rounded px-3 py-2 font-mono text-sm">
                      {example.input}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Get this:
                    </label>
                    <div className="bg-white border border-gray-300 rounded px-3 py-2 text-center">
                      <InlineMath math={example.output} />
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 italic">
                  {example.description}
                </p>
              </div>
            ))}
          </div>

          {/* Quick Tips */}
          <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2 flex items-center">
              <span className="text-lg mr-2">💡</span>
              Quick Tips
            </h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Use spaces between symbols for better readability</li>
              <li>• Wrap complex expressions in curly braces {`{}`}</li>
              <li>• Preview your math as you type below the input field</li>
              <li>• Press spacebar after typing to trigger auto-conversion</li>
              <li>• Use parentheses () for grouping in functions</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Pro tip:</span> You can also type mathematical expressions naturally and they'll be converted automatically!
            </div>
            <button
              onClick={onClose}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MathInputHelp;